import { useContractFunction, useEthers } from "@usedapp/core";
import { ethers } from "ethers";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { ABI } from "../../abi";
import usePriceInWei from "../../hooks/usePriceInWei";
import { PageTemplate } from "../templates";
import { MerkleTree } from "merkletreejs";
import keccak256 from "keccak256";
import addresses from "../../claimable_addresses.json";
import useIsClaimed from "../../hooks/useIsClaimed";
import useMintedTokenIdList from "../../hooks/useMintedTokenIdList";
import useIsOnSale from "../../hooks/useIsOnSale";

const PurchasePage = () => {
  const contractAddress = process.env.REACT_APP_NFT_ADDRESS ?? "";
  const { account, chainId } = useEthers();
  const price = usePriceInWei();
  const isClaimed = useIsClaimed(account);
  const isOnSale = useIsOnSale();
  const [tokenId, setTokenId] = useState(-1);

  const mintedIdList = useMintedTokenIdList();
  const mintedNumbers: number[] =
    mintedIdList
      ?.map((id) => id.toNumber())
      .filter((id) => id < 9900) ?? [];
  const onSaleTokenIdList = useMemo(() => {
    return [...Array(9900)]
      .map((_, index) => index)
      .filter((id) => !mintedNumbers.includes(id))
      .sort(() => 0.5 - Math.random());
  }, [mintedNumbers]);

  const amountNumber = useMemo(() => {
    return 9900 - mintedNumbers.length ?? 0;
  }, [mintedNumbers]);

  const { isClaimable, merkleProof } = useMemo(() => {
    if (isClaimed) {
      return {
        isClaimable: false,
        merkleProof: [],
      };
    }
    const tree = new MerkleTree(addresses, keccak256, {
      hashLeaves: true,
      sortPairs: true,
    });
    const leaf = keccak256(account ?? "");
    const isClaimable = tree.getLeafIndex(leaf) !== -1;
    const merkleProof = tree.getHexProof(leaf);
    return { isClaimable, merkleProof };
  }, [account, isClaimed]);

  const [getTokenIds, setGetTokenIds] = useState<number[]>([]);

  const contract = new ethers.Contract(contractAddress, ABI);
  const { send: claim, state: claimState } = useContractFunction(
    contract,
    "claim"
  );
  const { send: buyBundle, state: buyBundleState } = useContractFunction(
    contract,
    "buyBundle"
  );
  const { send: mint, state: mintState } = useContractFunction(
    contract,
    "mint"
  );

  useEffect(() => {
    if (onSaleTokenIdList.length === 0) {
      return;
    }
    if (tokenId !== -1 && !mintedNumbers.includes(tokenId)) {
      return;
    } else {
      setTokenId(onSaleTokenIdList[0]);
    }
  }, [tokenId, onSaleTokenIdList, setTokenId]);

  useEffect(() => {
    if (buyBundleState.receipt === undefined || getTokenIds.length !== 0) {
      return;
    }
    const tokens: number[] = [];
    // @ts-ignore
    for (let i = 0; i < buyBundleState.receipt.events.length; i++) {
      // @ts-ignore
      tokens.push(buyBundleState.receipt.events[i].args.tokenId.toNumber());
    }
    setGetTokenIds(tokens);
  }, [buyBundleState, getTokenIds, setGetTokenIds]);

  return (
    <PageTemplate
      isConnected={!!account}
      claim={claim}
      buyBundle={buyBundle}
      buyBundleTransactionStatus={buyBundleState}
      mintStatus={mintState}
      mint={mint}
      isPurchasing={false}
      transactionStatus={claimState}
      chainId={chainId}
      account={account}
      getTokenId={tokenId}
      getTokenIds={getTokenIds}
      isSoldOut={amountNumber == 0}
      price={price}
      amountNumber={amountNumber}
      isClaimable={isClaimable}
      isOnSale={isOnSale}
      merkleProof={merkleProof}
    />
  );
};

export default PurchasePage;
